'use client';

import { useEffect, useRef, useState } from 'react';

import { optimizedImageAttributes } from '../../util/optimizedImage';
import type { ImageProps } from './Image.types';

/** Renders nothing if src is missing or image fails to load. */
export const Image = ({ src, alt, sizes, onError, ...otherProps }: ImageProps) => {
  const [failed, setFailed] = useState<'optimized' | 'original' | null>(null);
  const ref = useRef<HTMLImageElement>(null);

  // React attaches the error listener only during hydration. A server-rendered image that
  // already failed before that point never fires onError, so replay the error once mounted.
  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth === 0) {
      img.dispatchEvent(new Event('error'));
    }
  }, []);

  if (!src || failed === 'original') {
    return null;
  }

  const optimized = typeof src === 'string' && failed === null ? optimizedImageAttributes(src, sizes) : { src };

  return (
    // biome-ignore lint/performance/noImgElement: customer-supplied S3/CloudFront URLs — next/image throws for domains not listed in remotePatterns
    <img
      ref={ref}
      {...optimized}
      alt={alt ?? ''}
      {...otherProps}
      onError={(e) => {
        if ('srcSet' in optimized && optimized.srcSet) {
          setFailed('optimized');
          return;
        }
        setFailed('original');
        onError?.(e);
      }}
    />
  );
};
